/**
 * Single source of truth for the backend API base URL.
 *
 * Why `import.meta.env` and not a custom `define` global: Vite reliably inlines
 * `VITE_*` values at build/dev time, whereas custom globals proved fragile and
 * were silently ignored when a stale compiled vite.config.js shadowed the .ts.
 *
 * Read through `?.` because Vite is not the only loader: under Jest (ts-jest
 * ESM) `import.meta` exists but carries no `env`, so a direct property read
 * threw at import time and took down every suite that transitively imported
 * `services/api`. In a Vite build `env` is always present, so it costs nothing.
 */
const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
const configuredOrigin = env?.VITE_API_URL || "http://localhost:5000";
const prefix = "/api/v1";

/** Hostnames that only ever mean "this same device". */
const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]", "::1", ""]);

/**
 * Resolve the API origin for the device actually running the page.
 *
 * THE BUG THIS FIXES
 * `VITE_API_URL` is inlined into the bundle at build time. With the usual
 * `http://localhost:5000`, the desktop works (the page and the API share a
 * machine) but every OTHER device fails: open the app from a phone at
 * `http://192.168.1.8:3100` and the bundle still tells it to call
 * `http://localhost:5000` — which on a phone is the *phone itself*, where
 * nothing is listening. Login appears broken on mobile while working on the
 * desktop with identical credentials.
 *
 * THE RULE
 * If the configured origin points at loopback but the page is being served
 * from some other host, that configuration cannot be what the user meant —
 * keep the configured port and scheme, but talk to the host the page came
 * from. Any explicitly configured non-loopback origin (a real deployment) is
 * always honoured untouched.
 */
/** Exported as a pure function so the rule can be tested directly. Mutating
 *  `window.location` in jsdom is unreliable, and this logic is too easy to get
 *  wrong to leave uncovered. */
export const resolveApiOrigin = (
    configured: string,
    pageHostname: string,
    pageProtocol: string
): string => {
    let parsed: URL;
    try {
        parsed = new URL(configured);
    } catch {
        return configured;
    }

    const configuredIsLoopback = LOOPBACK.has(parsed.hostname);
    const pageIsLoopback = LOOPBACK.has(pageHostname);

    // Only rewrite the loopback-config-but-remote-page case. Everything else —
    // a real API domain, or genuinely browsing on the host machine — is left
    // exactly as configured.
    if (configuredIsLoopback && !pageIsLoopback) {
        // Match the page's scheme too: a page served over https cannot call
        // http without the browser blocking it as mixed content.
        const scheme = pageProtocol === "https:" ? "https:" : parsed.protocol;
        return `${scheme}//${pageHostname}:${parsed.port || "5000"}`;
    }

    // Same machine, but the dev server is now on https so the microphone works
    // on LAN. `VITE_API_URL` still says http://localhost:5000, and the browser
    // blocks that as mixed content — silently, so every request just fails.
    // Upgrading the scheme is safe because the dev certificate covers loopback
    // too. Only done for loopback: forcing https on a real configured http API
    // would break a deployment that genuinely serves plain http.
    if (configuredIsLoopback && pageProtocol === "https:" && parsed.protocol === "http:") {
        return `https://${parsed.hostname}:${parsed.port || "5000"}`;
    }

    return configured.replace(/\/$/, "");
};

const resolveOrigin = (): string => {
    if (typeof window === "undefined" || !window.location) {
        return configuredOrigin; // SSR / test environments
    }
    return resolveApiOrigin(
        configuredOrigin,
        window.location.hostname,
        window.location.protocol
    );
};

export const API_ORIGIN = resolveOrigin();

export const API_BASE_URL = `${API_ORIGIN.replace(/\/$/, "")}${prefix}`;

export const SUPER_ADMIN_SLUG = (env?.VITE_SUPER_ADMIN_SLUG || "superadmin")
    .trim()
    .toLowerCase();

/**
 * The name the product wears in public.
 *
 * The platform tenant's own record is called "SuperAdmin" — that is an internal
 * label for the operator account, not a brand, and showing it to a student says
 * they are signed in as a super admin. Anywhere a non-admin can see the tenant
 * name, this is what they get instead.
 */
export const PUBLIC_BRAND_NAME = "Green Agentick";
